/**
 * Launch Darkly flags
 * @example Usage:
 * import { LD } from 'src/app/constants/launch-darkly-flags';
 * import { useLDFlag } from 'src/app/hooks/use-ld-flag';
 *
 * const thingRollout = useLDFlag(LD.THING_ROLLOUT, false);
 * or
 * const thingRollout = useLDFlag<boolean>(LD.THING_ROLLOUT);
 *
 * @function useLDFlag - src/app/hooks/use-ld-flag.ts
 *
 * Deprecation/Removal Steps:
 * 1. Add example deprecated comment above the flag you are removing.
 * 2. Right click key and select "Find All References" to find all usages.
 * 3. Remove the flag from the enum.
 *
 * @see Naming Conventions
 * https://dutchie.atlassian.net/wiki/spaces/EN/pages/19934543881/LaunchDarkly+Flag+Naming+Convention
 * `{charter}.{module/team}.{name}.{rollout|experiment|entitlement|operational}`
 */
export enum LD {
  /**
   * @deprecated Fully enabled
   */
  A_THING_ROLLOUT_DEPRECATED_EXAMPLE = 'backoffice.platform.deprecated-example.rollout',
  ADD_ECOM_CATEGORY_TO_CATALOG = 'pos.backoffice.add-ecom-category-to-catalog-online-details.rollout',
  ASYNC_REPORTS_ROLLOUT = 'pos.backoffice.async-reports.rollout',
  BACKGROUND_JOB_STATUS = 'pos.traceability.background-job-status.rollout',
  BIOTRACK_AUDIT_VOID_LAB_SAMPLES = 'pos.traceability.biotrack-audit-void-lab-samples.rollout',
  BIOTRACK_DRY_INVENTORY = 'pos.inventory.biotrack-dry-inventory.rollout',
  BIOTRACK_FL_CALCULATE_CONVERTED_OUTPUT_QUANTITY = 'pos.traceability.biotrack-fl-calculate-converted-output-quantity.rollout',
  BIOTRACK_INVENTORY_AUDIT = 'pos.traceability.biotrack-inventory-audit.rollout',
  BIOTRACK_INVENTORY_DESTRUCTION_PERMS = 'pos.traceability.biotrack-destruction-permissions.rollout',
  BIOTRACK_NY_AUTH_EMAIL_NOTIFICATIONS = 'pos.traceability.biotrack-ny-auth-email-notifications.rollout',
  BIOTRACK_ONBOARDING_TAB = 'pos.traceability.biotrack-onboarding-tab.rollout',
  BIOTRACK_SAMPLE_GUARDRAILS = 'pos.traceability.biotrack-sample-guardrails.rollout',
  BIOTRACK_V4_PICKUP_MANIFESTS = 'pos.traceability.biotrack-v4-pickup-manifests.rollout',
  BLAST_PRODUCTS_RESTRICT_ACTIONS = 'pos.traceability.blast-products-restrict-actions.rollout',
  BRAND_CONNECT_ENABLED = 'pos.brands.brand-connect-backoffice.rollout',
  BULK_UPDATE_ALLOW_ADMIN_UPDATES = 'backoffice.tools.allow_admin_bulkupdates.entitlement',
  BULK_UPDATE_CUSTOMER_PAGINATION = 'pos.backoffice.bulk-update-pagination.rollout',
  BULK_UPDATE_LOYALTY_PERMISSION_ROLLOUT = 'pos.customers.superuser-permission.bulk-loyalty-update.rollout',
  BULK_UPDATE_NEW_COLUMNS = 'pos.traceability.add-fields-to-bulk-updater-export.rollout',
  BULK_UPDATE_PRODUCTS_PUSH_TO_STATE_ROLLOUT = 'pos.traceability.bulk-update-products-push-to-state.rollout',
  BULKINVENTORYV3 = 'pos.backoffice-tools.bulk-updater.bulkupdaterinventoryV3.rollout',
  BULKINVENTORYV4 = 'pos.backoffice-tools.bulk-updater.bulkupdaterinventoryV4.rollout',
  CATALOG_GRID_NEW_COLUMNS = 'pos.traceability.catalog-grid-new-columns.rollout',
  CHARTJS_COMPONENTS_ROLLOUT = 'pos.backoffice.customers.chartjs-refactor.rollout',
  CREATE_BROWSER_ROUTER_ROLLOUT = 'backoffice.update-router-to-create-browser-router.rollout',
  CREATE_PRODUCTS_FROM_BRAND_CATALOG_PRODUCTS = 'pos.backoffice.create-products-from-brand-catalog-products.rollout',
  CULTIVATION_REFACTOR_ROLLOUT = 'pos.backoffice.cultivation-refactor.rollout',
  CUSTOMER_DEDUPE_KILLSWITCH_ROLLOUT = 'pos.backoffice.customer-dedupe-tool-killswitch.operational',
  CUSTOMER_DETAILS_LOYALTY_ROLLOUT = 'pos.backoffice.customers.customer-details-loyalty-updates.rollout',
  CUSTOMERS_CAMPAIGN_DEFAULT_TEMPLATES_FIX = 'pos.customers.campaigns-default-templates-fix.rollout',
  CUSTOMERS_SEGMENTS_IS_PAY_BY_BANK_USER_ATTRIBUTE = 'pos.backoffice.customers.is-pay-by-bank-user-attribute.rollout',
  CUSTOMERS_SEGMENTS_PAY_BY_BANK_ATTRIBUTE = 'pos.backoffice.customers.pay-by-bank-segment-attribute.rollout',
  CUSTOMERS_SEGMENTS_REPORTS_DEMO_DATA_ROLLOUT = 'pos.backoffice-customers.segments-overview-demo-data.rollout',
  CUSTOMERS_WATERFALL_CAMPAIGNS = 'pos.backoffice.customers.waterfall-campaigns.rollout',
  DAYS_SUPPLY_BASED_PURCHASE_LIMITS = 'pos.traceability.days-supply-based-purchase-limits.rollout',
  DISABLE_USER_PASSWORD_LOGIN = 'sso.disable-user-password-login',
  DISCOUNT_BY_ORDER_TYPE_MENU_TYPE = 'core.cats-pos.discount-by-order-type-menu-type.rollout',
  DISCOUNT_PAYMENT_RESTRICTIONS = 'core.cats.discount-payment-restrictions.operational',
  DISCOUNT_WIZARD = 'core.cats-pos.discount-wizard.rollout',
  DISCOUNT_WIZARD_GAMIFICATION = 'core.cats-pos.discount-wizard-gamification-preview.rollout',
  DISCOUNT_WIZARD_GLOBAL_ROLLOUT = 'core.backoffice.discount-wizard-global.rollout',
  DISCOUNTS_LOYALTY_MULTIPLIER_FIX = 'pos.backoffice.customers.discounts.loyalty-multiplier-fix.rollout',
  DUTCHIE_AGENT = 'pos.platform.dutchie-agent.rollout',
  DUTCHIE_INTELLIGENCE_SERVICE_CONTEXT_DEBUG_BUTTON_ROLLOUT = 'pos.backoffice.dutchie-intelligence-service.context-debug-button.rollout',
  DUTCHIE_INTELLIGENCE_SERVICE_ROLLOUT = 'pos.backoffice.dutchie-intelligence-service.rollout',
  DUTCHIE_INTELLIGENCE_SUMMARY_BANNER_ROLLOUT = 'pos.backoffice.dutchie-intelligence-service.summary-banner.rollout',
  EDIT_BIOTRACK_INVENTORY_INGREDIENTS_FOR_LABELING = 'pos.inventory.print-biotrack-inventory-ingredients-on-label.rollout',
  EDIT_COMPANY_FIX_FOR_LOYALTY_SETTINGS = 'pos.customers.edit-company-fix-for-loyalty-settings.rollout',
  EMAIL_MARKET_AUDIENCE_SIZE_FIX = 'pos.customers.email-marketing-final-review-audience-size-fix.rollout',
  EMAIL_MARKETING_ABANDONED_CART_V2 = 'pos.marketing.abandoned-cart-v2.rollout',
  EMAIL_MARKETING_EDIT_CAMPAIGN_RACE_CONDITION_FIX = 'pos.marketing.campaign.email-editor-race-condition-fix',
  EMAIL_TEST_SEND_MULTIPLE_RECIPIENTS = 'pos.marketing.email.test-send-multiple-recipients',
  EXTENDED_LAB_RESULTS_SUPPORT = 'pos.traceability.extended-lab-results-support.rollout',
  FILTER_CUSTOMERS_BY_GROUP = 'pos.customers.filter-customers-by-group.rollout',
  FINANCIALS_TAB_ROLLOUT = 'pos.backoffice.financials-tab.rollout',
  GENERAL_BACKOFFICE_HEADER_BANNER = 'backoffice.platform.general-header-banner.entitlement',
  GENERIPAY_FUSION = 'fintech.retail-payments.fusion-generipay-adapter.rollout',
  GENERIPAY_SPIN = 'fintech.retail-payments.spin-generipay-adapter.rollout',
  GENERIPAY_TFI = 'fintech.retail-payments.TFI-generipay-adapter.rollout',
  GENERIPAY_VECTOR = 'fintech.retail-payments.vector-generipay-adapter.rollout',
  GET_INTEGRATIONS_V5 = 'pos.get-integrations-v5.rollout',
  GQL_ERROR_MITIGATION = 'pos.backoffice.gql-error-mitigation.rollout',
  GQL_RATE_LIMITING = 'pos.backoffice.gql-rate-limiting.rollout',
  HIDE_FINANCIALS_TAB = 'pos.reporting.hide-financials-tab.rollout',
  HOMEPAGE_MARKETING = 'pos.backoffice.homepage.experiment',
  IMPROVE_COPY_PRODUCT = 'pos.backoffice.improve-copy-product.rollout',
  INDIRECT_REVENUE_ATTRIBUTION_DUMMY_DATA_ROLLOUT = 'pos.customers.indirect-revenue-attribution-dummy-data.rollout',
  INTELLIGENCE_CENTER = 'pos.backoffice.intelligence-center.rollout',
  INVENTORY_AUDIT_UPDATE_PACKAGE_CACHE_ROLLOUT = 'pos.inventory.audit-update-package-cache.rollout',
  INVENTORY_RECEIVE_VALIDATE_DATES_WITHIN_RANGE_ROLLOUT = 'pos.inventory.receive-validate-dates-within-range.rollout',
  INVENTORY_REPORTS_V2_ROLLOUT = 'platform.reports.inventory-performance-report-v2.rollout',
  INVENTORY_STORE_DEPRECATION = 'pos.backoffice.deprecate-get-inventory.rollout',
  INVOICES_ROLLOUT = 'pos.traceability.biotrack-ny.invoices.rollout',
  IRL_EVENTS = 'pos.customers.events.rollout',
  KILL_SWITCH_BY_DOMAIN_OR_PAGE = 'pos.backoffice.kill-switch-by-domain.killswitch',
  LAB_WIZARD_2 = 'pos.traceability.lab-wizard-v2.rollout',
  LABEL_LIBRARY_STATE_PACKS_INDICATOR = 'pos.backoffice-tools.labellibrary.state-packs-indicator.rollout',
  LAGOS_POC = 'growth.brands.lagos-poc.rollout',
  LEGACY_DISCOUNT_UPGRADE_BANNER = 'core.cats.discounts.legacy-upgrade-banner.rollout',
  LEISUREPAY_PARAM_DOWNLOAD = 'fintech.retail-payments.trigger-param-donwload.rollout',
  LOG_OUT_ON_SESSION_REVOCATION = 'pos.backoffice.log-out-on-session-revocation.rollout',
  LOGIN_MARKETING = 'pos.backoffice.login.experiment',
  LOGROCKET_CHAT_WIDGET_FIX_ROLLOUT = 'pos.backoffice.logrocket-chat-widget-fix.rollout',
  LOYALTY_AWARDS_AS_DISCOUNTS = 'core.cats-pos.loyalty-awards-as-discounts.rollout',
  MANAGE_RETAIL_ID_UI = 'pos.inventory.manage-retail-id-ui.rollout',
  MANIFEST_OPTIONS_METRC_ONBOARDING = 'pos.traceability.show-metrc-onboarding.entitlement',
  MANUAL_EXTERNAL_LOYALTY_DISCOUNTS = 'core.cats-pos.manual-external-loyalty-discounts.rollout',
  METRC_HIDE_CAPTURE_RECEIPT_IDS = 'pos.traceability.metrc-bypass-capture-receipt-ids.rollout',
  METRC_ONBOARDING_V2 = 'pos.traceability.metrc-onboarding-v2.rollout',
  METRC_PRODUCT_VALIDATION = 'pos.traceability.metrc-product-validation.rollout',
  /**
   * Controls access to Metrc Receipt Audit V2 feature
   * 0 = Off (feature disabled)
   * 1 = On for superusers only (view and actions available)
   * 2 = On for all users but view only (no bulk actions shown)
   * 3 = On for all users (view and bulk actions available)
   */
  METRC_RECEIPT_AUDIT_V2_ACCESS = 'pos.traceability.metrc-receipt-audit-v2-access.rollout',
  METRC_RETAIL_ID = 'pos.traceability.metrc-retail-id.rollout',
  METRC_SYNC_V2 = 'pos.traceability.metrc-sync-v2.rollout',
  MLSR_SALES_TABS = 'platform.reports.mlsr-sales-tabs.rollout',
  MOVE_METRC_PULL_TOOL = 'pos.backoffice.move-metrc-pull-tool.rollout',
  MOVE_METRC_PULL_TOOL_ROLLOUT = 'pos.backoffice.move-metrc-pull-tool.rollout',
  NON_SANDBOX_BANNER_ALERT = 'backoffice.platform.non-sandbox-banner-alert.rollout',
  PAY_BY_BANK_EXPRESS_LANE_ROLLOUT = 'fintech.retail-payments.pbb-express-lane.rollout',
  PAYMENTS_HUB_UPSELLS_POC = 'fintech.retail-payments.dutchie-hub-upsell-poc.rollout',
  PLATFORM_REPORTS_BUDTENDER_FULFILLMENT_ROLLOUT = 'platform.reports.budtender-fulfillment.rollout',
  POS_TRACEABILITY_BIOTRACK_REQUEST_DATE = 'pos.traceability.biotrack-request-date.rollout',
  PRICE_COMPARISON_MIN_PRICE_THRESHOLD = 'pos.backoffice.intel-center-price-comparison-min-price-threshold.operational',
  PRICE_COMPARISON_WIDGET_ROLLOUT = 'pos.backoffice.intel-center-price-comparison.rollout',
  PRICING_INSIGHTS_ROLLOUT = 'pos.cats.pricing-insights.rollout',
  PRICING_TIER_PREVIEW_ROLLOUT = 'core.cats.pricing-tier-preview.rollout',
  PRODUCT_EFFECTS_BACKOFFICE_ROLLOUT = 'pos.products.effects.backoffice.rollout',
  PROTECT_SUPERUSER_URLS = 'pos.platform.protect-superuser-endpoint.rollout',
  PUSH_BATCH_TO_METRC = 'pos.traceability.push-batch-to-metrc.rollout',
  PUSH_MARKETING_FLAG_ROLLOUT = 'pos.marketing.push-campaigns.rollout',
  PUSHER_ENABLED_SETTINGS_UPDATES = 'Pos.Pusher-Enabled-Settings-Updates',
  REFRESH_PAGE_ON_LSP_OR_LOC_CHANGE = 'pos.backoffice.refresh-page-on-lsp-or-loc-change.operational',
  REFRESH_POTENCY_BY_EXTERNAL_CATEGORY = 'pos.traceability.refresh-potency-by-external-category.rollout',
  RESTRICT_BY_CUSTOMER_TYPE = 'core.cats-pos.enable-customer-type-targeting-for-fees.rollout',
  RESTRICT_BY_FEE_BY_DELIVERY_TYPE = 'core.cats-pos.use-online-context-in-fees.rollout',
  SENDGRID_ONBOARDING_ROLLOUT = 'pos.marketing.sendgrid-onboarding.rollout',
  SERVER_MIGRATION_CONFIG = 'pos.platform.database-migration-configs.operational',
  SHOW_LOC_IN_USER_EVENT_LOGS = 'pos.platform.add-location-to-user-event-logs',
  SHOW_RETAIL_ID_TASK = 'pos.traceability.show-retail-id-task.rollout',
  SMALL_BUSINESS_PACKS = 'pos.tools.small-business-packs',
  SMART_TAG_WIZARD_GLOBAL_ROLLOUT = 'core.backoffice.smart-tag-wizard-global.rollout',
  SMART_TAGS_REFRESH_ALL_LSP_WIDE = 'core.cats.smart-tags.refresh-all-lsp-wide.rollout',
  SMART_TAGS_ROLLOUT = 'core.cats.smart-tags.rollout',
  SMS_MARKETING_FLAG_ROLLOUT = 'pos.marketing.sms.feature-flag-rollout',
  STATEPACKS_REGISTER_ADJUSTMENTS_ROLLOUT = 'pos.backoffice-tools.state-packs.registeradjustments.rollout',
  STATEPACKS_RETURN_REASONS_ROLLOUT = 'pos.backoffice-tools.state-packs.returnreasons.rollout',
  STATIC_REPORTS_EXCLUDE_LIST = 'core.reports.static-reports-exclude-list.operational',
  STRAIN_TYPE_VISIBILITY_ROLLOUT = 'pos.inventory.strain-type-visibility.rollout',
  TABS_SCROLL_BUTTONS_FIX = 'pos.backoffice.tabs.scroll-buttons-fix.rollout',
  TEMPLATE_UPDATE_FIX_ROLLOUT = 'pos.marketing.template.update-fix.rollout',
  TIME_PICKER_VALIDATION_ROLLOUT = 'core.cats.time-picker-validation.rollout',
  USE_GET_LABELS_V2 = 'pos.inventory.use-get-labels-v2.rollout',
  USE_GET_PRODUCTS_LITE_FOR_DROPDOWNS = 'pos.inventory.use-get-products-lite-for-dropdowns.rollout',
  USE_GOOD_CANNA_INTEGRATION_ROLLOUT = 'fintech.retail-payments.good-canna-integration-enabled.rollout',
  USE_LEISUREPAY_ROLLOUT = 'fintech.retail-payments.leisure-pay.rollout',
  USE_MESSAGE_LEVEL_UTM_FIELDS = 'pos.backoffice.message-level-utm-params.rollout',
  USE_NEW_BULK_UPLOAD = 'pos.backoffice.use-new-bulk-upload.rollout',
  USE_PATIENT_TRANSACTIONS_V5 = 'fintech.retail-payments.use-patient-transactions-v5.rollout',
  USE_REQUIRES_TERMINAL_CONFIG_ATTRIBUTE = 'fintech.retail-payments.payment-integration-definition-requires-terminal-config-attribute.rollout',
  USE_STANDARD_ALLERGENS = 'backoffice.products.use-standard-allergens',
  USER_ID_IN_REPORT_EXPORT = 'pos.backoffice.include-user-id-in-export.rollout',
  VALIDATE_FIREBASE_PROJECT_ID = 'pos.customers.validate-firebase-id.rollout',
  WARN_ON_CONVERSION_DISCREPANCY = 'pos.traceability.warn-on-conversion-discrepancy.rollout',
}
